import React, { useMemo } from 'react';
import { useRouter } from 'next/router';

import { useQuery, QueryClient, dehydrate } from 'react-query';
import { Pagination } from '@mui/material';

import { GetToolkits, GetToolkitCategories } from 'services/toolkits';
import { queryClientConfig } from '@core/configs/queryClientConfig';
import { setSsrTokens } from 'utils/authentication';

import { getToolkitsProps } from 'router/seoprops';
import { NextSeo } from 'next-seo';

import ToolkitCard from 'components/pages/toolkits/toolkitcard/ToolkitCard';
import ToolkitsFilters from 'components/pages/toolkits/toolkitsfilters/ToolkitsFilters';
import FAQ from 'components/common/faq/FAQ';
import Spinner from '@core/components/spinner/Spinner';

import { toolkitsFaq } from 'constants/explore-faq.const';

import classes from './toolkits.module.scss';

const PAGE_SIZE = 9;

const buildQueryParams = (query) => {
    const params = {};
    if (query?.page) params.page = parseInt(query.page, 10) || 1;
    if (query?.category) params.category = query.category;
    if (query?.has_access !== undefined && query?.has_access !== '') params.has_access = query.has_access;
    return params;
};

const ToolkitsExplore = () => {
    const router = useRouter();
    const seoProps = getToolkitsProps();

    const filters = useMemo(() => buildQueryParams(router.query), [router.query]);
    const currentPage = filters.page || 1;

    const { data: categoriesData } = useQuery(
        ['toolkit-categories'],
        GetToolkitCategories,
        { staleTime: 5 * 60 * 1000 }
    );

    const { data, isLoading } = useQuery(
        ['toolkits', filters],
        () => GetToolkits(filters),
        { keepPreviousData: true }
    );

    const toolkits = data?.data?.results || [];
    const totalCount = data?.data?.count || 0;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const categories = categoriesData?.data || [];

    const updateQuery = (next) => {
        const newQuery = { ...router.query, ...next };
        // strip undefined values
        Object.keys(newQuery).forEach((k) => {
            if (newQuery[k] === undefined || newQuery[k] === '') delete newQuery[k];
        });
        router.replace({ pathname: '/toolkits', query: newQuery }, undefined, { shallow: true });
    };

    const handleFilterChange = (next) => {
        // reset to page 1 when filters change
        updateQuery({ ...next, page: undefined });
    };

    const handlePageChange = (_, page) => {
        updateQuery({ page: page === 1 ? undefined : page });
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    };

    return (
        <>
            <NextSeo {...seoProps} />
            <div className="container px-xl-0">
                <div className="row d-flex justify-content-center">
                    <div className="col-xl-10 col-12">
                        <div className={classes['wrapper']}>
                            <div className={classes['header']}>
                                <h2>Explore Toolkit</h2>
                                <p>Practical tools to help you work faster — SOPs, checklists, calculators, and more</p>
                            </div>

                            <ToolkitsFilters
                                categories={categories}
                                filters={filters}
                                onChange={handleFilterChange}
                            />

                            {isLoading ? (
                                <div className={classes['loading']}>
                                    <Spinner />
                                </div>
                            ) : (
                                <>
                                    <div className={classes['card-grid']}>
                                        {toolkits.length > 0 ? (
                                            toolkits.map((toolkit) => (
                                                <ToolkitCard key={toolkit.id} toolkit={toolkit} />
                                            ))
                                        ) : (
                                            <div className={classes['empty']}>
                                                No toolkits match your filters.
                                            </div>
                                        )}
                                    </div>

                                    {totalPages > 1 && (
                                        <div className={classes['pagination']}>
                                            <Pagination
                                                count={totalPages}
                                                page={currentPage}
                                                onChange={handlePageChange}
                                                variant="outlined"
                                                shape="rounded"
                                                color="primary"
                                            />
                                        </div>
                                    )}
                                </>
                            )}

                            <div className={classes['faq-wrapper']}>
                                <FAQ data={toolkitsFaq} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export async function getServerSideProps(context) {
    setSsrTokens(context);
    const queryClient = new QueryClient(queryClientConfig);
    const filters = buildQueryParams(context.query);

    try {
        const [toolkitsResponse, categoriesResponse] = await Promise.all([
            GetToolkits(filters),
            GetToolkitCategories(),
        ]);
        await queryClient.prefetchQuery(['toolkits', filters], () => ({ data: toolkitsResponse.data }));
        await queryClient.prefetchQuery(['toolkit-categories'], () => ({ data: categoriesResponse.data }));
    } catch (e) {
        // ignore — client will retry
    }

    return {
        props: {
            dehydratedState: dehydrate(queryClient),
        },
    };
}

export default ToolkitsExplore;
